// src/contexts/AuthContext.js
import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import logger from '../utils/logger';
import { API_URL } from '../config';

export const AuthContext = createContext();

// How long cached profile data stays valid before a background refresh (1 hour)
const CACHE_TTL = 3600000;

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isPremiumUser, setIsPremiumUser] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // ── Helpers ────────────────────────────────────────────────

  const applySession = useCallback((profile) => {
    setUser(profile);
    setIsLoggedIn(true);
    setIsPremiumUser(!!profile?.isPremium);
    // Keep localStorage mirrors in sync (Header reads these directly)
    localStorage.setItem('user', JSON.stringify(profile));
    localStorage.setItem('isPremiumUser', String(!!profile?.isPremium));
    localStorage.setItem(
      'authData',
      JSON.stringify({ user: profile, isPremium: !!profile?.isPremium, lastFetched: Date.now() })
    );
  }, []);

  const clearSession = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('authData');
    localStorage.removeItem('isPremiumUser');
    setIsLoggedIn(false);
    setIsPremiumUser(false);
    setUser(null);
  }, []);

  // ── Profile fetch (FIXED) ─────────────────────────────────
  // The backend's /api/users/profile returns the user document DIRECTLY,
  // e.g. { _id, email, name, profilePic, isPremium, createdAt }.
  // The old code did `const { user, isPremium } = response.data` which made
  // `user` undefined forever → cache never validated → every page load
  // waited on the network. This version reads the response correctly.
  //
  // `blocking = false` means: refresh silently in the background without
  // flipping the app into a loading state.
  const fetchUserData = useCallback(
    async (token, forceFetch = false, blocking = true) => {
      const cachedData = JSON.parse(localStorage.getItem('authData') || '{}');
      const isCacheValid =
        Date.now() - (cachedData.lastFetched || 0) < CACHE_TTL && !!cachedData.user;

      // Serve instantly from cache when possible
      if (!forceFetch && isCacheValid) {
        setUser(cachedData.user);
        setIsLoggedIn(true);
        setIsPremiumUser(!!cachedData.isPremium);
        setIsLoading(false);
        return;
      }

      if (blocking) setIsLoading(true);
      try {
        const response = await axios.get(`${API_URL}/api/users/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const profile = response.data; // ← the user document itself
        applySession(profile);
      } catch (error) {
        const status = error.response?.status;
        logger.error('Failed to fetch user data:', error.response?.data || error.message);
        if (status === 401 || status === 403 || status === 404) {
          // Token is actually invalid/expired → real logout
          clearSession();
        }
        // Network errors / server asleep (Render cold start): keep the
        // existing session instead of randomly logging the user out.
      } finally {
        setIsLoading(false);
      }
    },
    [applySession, clearSession]
  );

  // ── Instant hydration from localStorage ───────────────────
  // LoginModal saves `token` + `user` and fires the authStateChanged event.
  // We use that saved user IMMEDIATELY (zero network wait), then refresh
  // the profile quietly in the background.
  const hydrateFromStorage = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return false;
    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
      if (storedUser) {
        applySession(storedUser);
        setIsLoading(false);
        return true;
      }
    } catch (e) {
      logger.error('Corrupt user data in storage, ignoring:', e);
    }
    return false;
  }, [applySession]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Show something instantly if we can, then validate in background
      const hydrated = hydrateFromStorage();
      fetchUserData(token, false, !hydrated);
    } else {
      setIsLoading(false);
    }

    const handleAuthChange = () => {
      const newToken = localStorage.getItem('token');
      if (newToken) {
        const hydrated = hydrateFromStorage();
        // Background refresh — never blocks the UI after login
        fetchUserData(newToken, true, !hydrated);
      } else {
        clearSession();
        setIsLoading(false);
      }
    };

    window.addEventListener('authStateChanged', handleAuthChange);
    return () => window.removeEventListener('authStateChanged', handleAuthChange);
  }, [fetchUserData, hydrateFromStorage, clearSession]);

  // ── Public API (unchanged names — nothing else breaks) ────

  // login(token, userObj?) — if the login response already gave us the user
  // (it always does), we use it instantly with NO extra network round trip.
  const login = useCallback(
    (token, userObj = null) => {
      localStorage.setItem('token', token);
      if (userObj) {
        applySession(userObj);
        setIsLoading(false);
        fetchUserData(token, true, false); // silent background sync
      } else {
        fetchUserData(token, true);
      }
    },
    [applySession, fetchUserData]
  );

  const logout = useCallback(() => {
    clearSession();
  }, [clearSession]);

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        isPremiumUser,
        user,
        isLoading,
        login,
        logout,
        setIsPremiumUser,
        fetchUserData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};